/**
 * Phase 57 Plan 57-04 — PORTAL-04
 *
 * Full-panel new-request form (NOT a modal) for the portal detail area.
 * Fields: title (required) + description (optional) + attachments dropzone.
 * Submits via the Phase 51 external create→triage path (useCreatePortalRequest).
 * On success calls onCreated with the new task id so the page can navigate
 * to the ConfirmationScreen.
 *
 * Quick task 260809-ofp (D-03) — the dropzone used to stage files and throw
 * them away on submit (mutation.mutate never sent them). It now:
 *   - accepts any file type up to a 25 MiB per-file cap (D-02 backend
 *     policy), flagging an oversized file client-side BEFORE any network
 *     call so the byte cost is never paid for a doomed upload;
 *   - passes the accepted (non-oversized) staged files into the create
 *     mutation, which uploads them sequentially after the task is created;
 *   - on a partial failure, keeps the panel open, names each failed file
 *     with its server-reported reason, marks the request as already
 *     created, and offers an explicit "Continue" control — re-pressing the
 *     primary control after that point does NOT create a second task
 *     (guarded by `createdTask` state).
 */
import { useState, useCallback, type FormEvent } from 'react';
import { useDropzone } from 'react-dropzone';
import { useCreatePortalRequest, type AttachmentResult } from '../../hooks/useCreatePortalRequest.js';
import { useExternalRequestCategories } from '../../hooks/useHelpdesk';

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MiB — mirrors the backend cap (D-02).

interface StagedFile {
  uid: number;
  file: File;
  /** Flagged client-side, before any network call, when file.size exceeds the cap. */
  oversized: boolean;
  /** Set after a partial-failure response names this file's server-side rejection reason. */
  errorMessage?: string;
  /** Set true once this file's upload is confirmed to have succeeded. */
  sent?: boolean;
}

let nextUid = 1;

export interface NewRequestFormProps {
  projectId: string;
  onCancel: () => void;
  onCreated: (task: { id: number }) => void;
}

export function NewRequestForm({ projectId, onCancel, onCreated }: NewRequestFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // MKS-339 — optional request category. '' means the client picked none,
  // which is always allowed; the field only appears at all when this project
  // has categories configured.
  const [categoryId, setCategoryId] = useState('');
  const { data: categories = [] } = useExternalRequestCategories(projectId);
  const [titleTouched, setTitleTouched] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [createdTask, setCreatedTask] = useState<{ id: number } | null>(null);
  const [hasFailures, setHasFailures] = useState(false);

  const mutation = useCreatePortalRequest((task, attachmentResults: AttachmentResult[]) => {
    setCreatedTask(task);
    const failures = attachmentResults.filter((r) => r.status === 'error');
    if (failures.length === 0) {
      onCreated(task);
      return;
    }
    setHasFailures(true);
    setStaged((prev) =>
      prev.map((f) => {
        if (f.oversized) return f;
        const failure = failures.find((fr) => fr.filename === f.file.name);
        if (failure) {
          return { ...f, sent: false, errorMessage: failure.message ?? 'Upload failed' };
        }
        return { ...f, sent: true };
      }),
    );
  });

  const onDrop = useCallback((accepted: File[]) => {
    setStaged((prev) => {
      const room = MAX_ATTACHMENTS - prev.length;
      if (room <= 0) return prev;
      const slice = accepted.slice(0, room).map((file) => ({
        uid: nextUid++,
        file,
        oversized: file.size > MAX_ATTACHMENT_BYTES,
      }));
      return [...prev, ...slice];
    });
  }, []);

  // 260809-ofp (D-03/D-02) — no `accept` filter: the backend now accepts any
  // content type on this surface, so the picker must offer every file type.
  const { getRootProps, getInputProps, isDragActive, open: openPicker } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  function removeFile(uid: number) {
    setStaged((prev) => prev.filter((f) => f.uid !== uid));
  }

  function handleContinue() {
    if (createdTask) onCreated(createdTask);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // 260809-ofp — the request was already created on a prior submit that hit
    // a partial attachment failure. Re-pressing the primary control must
    // continue to confirmation, NEVER create a second task.
    if (createdTask) {
      handleContinue();
      return;
    }
    setTitleTouched(true);
    const trimmedName = name.trim();
    if (!trimmedName || mutation.isPending) return;
    setErrorMessage(null);
    const trimmedDesc = description.trim();
    const filesToUpload = staged.filter((f) => !f.oversized).map((f) => f.file);
    mutation.mutate({
      projectId,
      input: {
        name: trimmedName,
        description: trimmedDesc.length > 0 ? trimmedDesc : null,
        categoryId: categoryId === '' ? null : Number(categoryId),
      },
      files: filesToUpload,
    });
  }

  // Surface mutation errors inline
  const displayError = mutation.isError
    ? ((mutation.error as Error)?.message ?? 'Could not submit. Try again.')
    : errorMessage;

  const canSubmit = createdTask
    ? true
    : name.trim().length > 0 && !mutation.isPending;

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-ms-text-muted mb-1">
            CUSTOMER PORTAL
          </p>
          <h1 className="text-3xl font-bold text-ms-text-strong">New request</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title field */}
          <div>
            <label
              htmlFor="request-title"
              className="block text-sm font-medium text-ms-text-strong mb-1"
            >
              What&#39;s your request?
              <span className="text-red-600 ml-0.5">*</span>
            </label>
            <input
              id="request-title"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTitleTouched(true)}
              placeholder="Give it a brief, clear title"
              maxLength={500}
              disabled={!!createdTask}
              className="w-full px-3 py-2 border border-ms-border rounded-lg text-ms-text focus:outline-none focus:ring-2 focus:ring-ms-orange focus:border-transparent placeholder:text-ms-text-muted disabled:opacity-60"
            />
            {titleTouched && !name.trim() && !createdTask && (
              <p className="text-xs text-red-600 mt-1">Title is required</p>
            )}
          </div>

          {/* MKS-339 — optional category. Hidden entirely when the project
              has no categories configured: an empty picker is noise, and the
              field is optional either way. */}
          {categories.length > 0 && (
            <div>
              <label
                htmlFor="request-category"
                className="block text-sm font-medium text-ms-text-strong mb-1"
              >
                What kind of request is it?
              </label>
              <select
                id="request-category"
                value={categoryId}
                disabled={!!createdTask}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-ms-border rounded-lg text-ms-text focus:outline-none focus:ring-2 focus:ring-ms-orange focus:border-transparent disabled:opacity-60"
              >
                <option value="">Not sure / something else</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Description field */}
          <div>
            <label
              htmlFor="request-description"
              className="block text-sm font-medium text-ms-text-strong mb-1"
            >
              Describe the issue or request
            </label>
            <textarea
              id="request-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Include steps to reproduce, error messages, or any other details that help"
              rows={6}
              disabled={!!createdTask}
              className="w-full px-3 py-2 border border-ms-border rounded-lg text-ms-text resize-none focus:outline-none focus:ring-2 focus:ring-ms-orange focus:border-transparent placeholder:text-ms-text-muted disabled:opacity-60"
            />
          </div>

          {/* Attachments dropzone */}
          <div>
            <p className="text-sm font-medium text-ms-text-strong mb-2">Attachments</p>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-default transition-colors ${
                isDragActive
                  ? 'border-ms-orange bg-orange-50'
                  : 'border-ms-border bg-ms-canvas hover:border-ms-text-muted'
              }`}
            >
              <input {...getInputProps({ 'aria-label': 'Attach files' })} />
              <p className="text-sm text-ms-text-secondary mb-2">
                Drag and drop files or click to browse
              </p>
              <button
                type="button"
                onClick={openPicker}
                disabled={!!createdTask}
                className="text-sm font-medium text-ms-orange hover:underline disabled:opacity-50"
              >
                Browse files
              </button>
              <p className="text-xs text-ms-text-muted mt-1">
                Any file type — 25 MB cap per file
              </p>
            </div>

            {/* Staged file chips */}
            {staged.length > 0 && (
              <ul className="mt-3 space-y-1">
                {staged.map(({ uid, file, oversized, errorMessage: fileError, sent }) => {
                  const isError = oversized || !!fileError;
                  return (
                    <li
                      key={uid}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                        isError
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-ms-surface-2 text-ms-text'
                      }`}
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="truncate max-w-xs">{file.name}</span>
                        {oversized && (
                          <span className="text-xs text-red-600">
                            Exceeds the 25 MB per-file cap — not uploaded
                          </span>
                        )}
                        {fileError && !oversized && (
                          <span className="text-xs text-red-600">{fileError}</span>
                        )}
                        {sent && !isError && (
                          <span className="text-xs text-ms-text-muted">Sent</span>
                        )}
                      </span>
                      {!createdTask && (
                        <button
                          type="button"
                          onClick={() => removeFile(uid)}
                          className="ml-4 text-ms-text-muted hover:text-ms-text flex-shrink-0"
                          aria-label={`Remove ${file.name}`}
                        >
                          ×
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Partial-failure warning block */}
          {hasFailures && createdTask && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">
                Your request was created, but some attachments failed to upload.
              </p>
              <p className="mt-1">
                Remove or retry the failed files above, or continue without them.
              </p>
            </div>
          )}

          {/* Inline error */}
          {displayError && (
            <p className="text-sm text-red-600" role="alert">
              {displayError}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={!canSubmit}
              className="bg-ms-orange text-white font-medium px-6 py-2 rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {mutation.isPending
                ? 'Submitting…'
                : createdTask
                  ? 'Continue'
                  : 'Submit request'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="text-ms-text-secondary hover:text-ms-text text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
